import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { UserRole } from '@lombok/shared';
import { useAuth } from '@/auth/AuthContext';
import { homePathForRole } from '@/auth/roles';
import { AuthSplash } from '@/components/AuthSplash';

/**
 * UX-ONLY route guard.
 *
 * ⚠️ This is NOT a security boundary. It only improves UX by redirecting signed-
 * out users to /login and hiding route groups a role shouldn't see. The AWS
 * backend authorizes EVERY request from the Cognito JWT and is the sole
 * authority — never assume a guarded route is safe just because this component
 * rendered. A determined user can reach any route; the API must still refuse.
 */
export function RoleGuard({
  allow,
  children,
}: {
  /** Roles permitted to view this route group (display gate only). */
  allow: readonly UserRole[];
  children: ReactNode;
}) {
  const { session, status } = useAuth();
  const location = useLocation();

  // Still restoring a persisted session — don't bounce to /login prematurely.
  if (status === 'loading') return <AuthSplash />;

  if (status !== 'authenticated' || !session) {
    // Preserve the intended destination so login can return the user there.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!allow.includes(session.user.role)) {
    // Authenticated but wrong group → send to the role's own home.
    return <Navigate to={homePathForRole(session.user.role)} replace />;
  }

  return <>{children}</>;
}
